import type { StructureView } from '@kronos/domain'
import { NodeThumb } from './NodeThumb'
import { qty } from './words'

/**
 * The sentence the diagram is a picture of, said in words first:
 * "this robot is made of 2 arms, 1 base and a camera — the shelf builds 0 of
 * them, and we are short 6 actuators".
 *
 * Each shortage is a button that selects the first node of that item in the
 * tree, because the next question after "short of what" is always "where".
 */
export function StructureSummary({
  view,
  buildable,
  quantity,
  stockKnown,
  compact,
  imageFor,
  onPick,
}: {
  view: StructureView
  buildable: number | null
  quantity: number
  stockKnown: boolean
  compact: boolean
  imageFor: (typeId: string) => string | null
  onPick: (typeId: string) => void
}): JSX.Element {
  const root = view.root
  const builds = view.toBuild.reduce((n, b) => n + b.count, 0)
  const forWhat = `${quantity === 1 ? 'one' : quantity} ${root.typeName}`

  return (
    <div className="st-summary">
      <p className="st-summary__sentence">
        One <strong>{root.typeName}</strong> is made of{' '}
        {root.children.map((c, i) => (
          <span key={c.key}>
            {i === 0 ? '' : i === root.children.length - 1 ? ' and ' : ', '}
            <strong>{qty(c.perParent)}</strong> × {c.typeName}
          </span>
        ))}
        {root.children.length === 0 ? 'nothing yet' : ''}.
      </p>

      {stockKnown && !compact ? (
        <div className="st-facts">
          <Fact
            n={buildable === null ? '—' : buildable >= 99 ? '99+' : String(buildable)}
            label="buildable from the shelf now"
            tone={buildable === 0 ? 'warning' : 'healthy'}
          />
          <Fact
            n={String(view.shortages.length)}
            label={view.shortages.length === 1 ? 'item short' : 'items short'}
            tone={view.shortages.length > 0 ? 'critical' : 'healthy'}
          />
          <Fact
            n={qty(builds)}
            label={builds === 1 ? 'assembly to build' : 'assemblies to build'}
            sub={view.toBuild.map((b) => `${qty(b.count)} × ${b.typeName}`).join(' · ')}
          />
        </div>
      ) : null}

      {stockKnown && view.shortages.length > 0 ? (
        <div className="st-summary__short">
          <span className="st-summary__label">Short for {forWhat}</span>
          {view.shortages.map((s) => (
            <button key={s.typeId} type="button" className="st-short-chip" onClick={() => onPick(s.typeId)}>
              <NodeThumb imageId={imageFor(s.typeId)} kind={s.kind} consumed={s.tracking === 'quantity'} size={24} />
              <span className="st-short-chip__n">{qty(s.short)}</span>
              <span className="st-short-chip__name">{s.typeName}</span>
            </button>
          ))}
        </div>
      ) : stockKnown ? (
        <p className="st-summary__ok">Everything {forWhat} needs is on the shelf.</p>
      ) : null}
    </div>
  )
}

function Fact({ n, label, sub, tone }: { n: string; label: string; sub?: string; tone?: 'healthy' | 'warning' | 'critical' }): JSX.Element {
  return (
    <div className={`st-fact${tone ? ` st-fact--${tone}` : ''}`}>
      <span className="st-fact__n">{n}</span>
      <span className="st-fact__text">
        <span className="st-fact__label">{label}</span>
        {sub ? <span className="st-fact__sub">{sub}</span> : null}
      </span>
    </div>
  )
}
